/**
 * Collapsible FAQ accordion grouped by section.
 * One-section-at-a-time expansion is deliberate (reduces visual noise).
 */

import { useState } from 'react';
import { FAQ_SECTIONS, FAQEntry } from './faqContent';

export function FAQAccordion() {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());

  const toggleSection = (id: string) => {
    setExpandedSection(prev => (prev === id ? null : id));
  };

  const toggleEntry = (sectionId: string, idx: number) => {
    const key = `${sectionId}-${idx}`;
    setExpandedEntries(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <div className="faq-accordion">
      {FAQ_SECTIONS.map(section => {
        const isOpen = expandedSection === section.id;
        return (
          <div key={section.id} className="faq-section">
            <button
              className="faq-section-header"
              onClick={() => toggleSection(section.id)}
              aria-expanded={isOpen}
              aria-controls={`faq-section-${section.id}`}
            >
              <span className="faq-section-chevron">{isOpen ? '▼' : '▸'}</span>
              <span className="faq-section-title">{section.title}</span>
              <span className="faq-section-count">{section.entries.length}</span>
            </button>
            {isOpen && (
              <div id={`faq-section-${section.id}`} className="faq-section-body">
                {section.entries.map((entry, idx) => (
                  <FAQEntryRow
                    key={idx}
                    entry={entry}
                    isOpen={expandedEntries.has(`${section.id}-${idx}`)}
                    onToggle={() => toggleEntry(section.id, idx)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface FAQEntryRowProps {
  entry: FAQEntry;
  isOpen: boolean;
  onToggle: () => void;
}

function FAQEntryRow({ entry, isOpen, onToggle }: FAQEntryRowProps) {
  return (
    <div className={`faq-entry ${isOpen ? 'faq-entry-open' : ''}`}>
      <button className="faq-entry-question" onClick={onToggle} aria-expanded={isOpen}>
        <span className="faq-entry-marker">{isOpen ? '−' : '+'}</span>
        <span>{entry.q}</span>
      </button>
      {isOpen && <div className="faq-entry-answer">{entry.a}</div>}
    </div>
  );
}
